import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Gift, ArrowRight, Star, Heart, Truck, Quote, Instagram, ShieldCheck, PackageOpen, PenLine,
  Sparkles, Palette, Building2,
} from "lucide-react";
import { useGet } from "@/lib/hooks";
import { formatINR } from "@/lib/api";
import { useSettings, useInstagram } from "@/context/SettingsContext";
import { useContent } from "@/lib/content";
import { Reveal } from "@/components/wp/Reveal";
import { SectionHeading } from "@/components/wp/SectionHeading";
import { ParcelCard } from "@/components/wp/ParcelCard";
import { ProductCard } from "@/components/wp/ProductCard";
import { MagneticButton } from "@/components/wp/MagneticButton";
import { GiftBox } from "@/components/wp/GiftBox";
import { ProtectedImage } from "@/components/wp/ProtectedImage";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";

const HOW_IT_WORKS = [
  { icon: Gift, title: "Choose a gift", copy: "Browse our curated parcels, or tell us who it's for and we'll help you find the one." },
  { icon: Heart, title: "Make it personal", copy: "Add a photo, a heartfelt note or a voice message — an optional, delightful touch." },
  { icon: PackageOpen, title: "We pack it beautifully", copy: "Every parcel is hand-packed, gift-ready, and shipped across India in 24–48 hours." },
];

const HIDE_COLLECTIONS = new Set(["founder", "under-599", "under-999"]);

export default function Home() {
  const navigate = useNavigate();
  const settings = useSettings();
  const content = useContent();
  const ig = useInstagram();
  const { data: collections } = useGet("/collections");
  const { data: parcels } = useGet("/parcels");
  const { data: products } = useGet("/products");
  const { data: founder } = useGet("/founder");
  const { data: reviews } = useGet("/reviews");
  const { data: silver } = useGet("/custom-silver");
  const { data: corporate } = useGet("/corporate");

  const hero = settings?.hero || {};
  const flaggedBest = (parcels || []).filter((p) => p.best_seller || p.featured);
  // Prefer flagged parcels, else visible parcels, else fall back to a curated slice of real products.
  const featuredParcels = (flaggedBest.length > 0 ? flaggedBest : (parcels || [])).slice(0, 8);
  const featuredProducts = (products || []).filter((p) => p.featured || p.best_seller);
  const featured = featuredParcels;
  // When no gift parcels are live, show featured products first, then top up with other
  // live products so the row looks complete (curated subset, never the full catalogue).
  const featuredFallback = featuredParcels.length === 0
    ? [...featuredProducts, ...(products || []).filter((p) => !featuredProducts.some((f) => f.id === p.id))].slice(0, 8)
    : [];
  const founderTeaser = (founder || []).slice(0, 6);
  const featuredCollections = (collections || []).filter((c) => !HIDE_COLLECTIONS.has(c.slug) && c.image).slice(0, 6);
  // Reviews only render when explicitly enabled in Admin (avoids fabricated social proof at launch).
  const showReviews = settings?.feature_flags?.reviews === true && (reviews || []).length > 0;

  return (
    <div>
      {/* ================= HERO ================= */}
      <section className="wp-hero-gradient wp-noise relative overflow-hidden">
        {[{ c: "bg-wp-blush-200", s: 240, x: "6%", y: "14%", d: 0 }, { c: "bg-wp-mint", s: 170, x: "82%", y: "10%", d: 1.2 }, { c: "bg-wp-gold-soft", s: 280, x: "72%", y: "62%", d: 0.6 }].map((b, i) => (
          <motion.span key={i} aria-hidden className={`pointer-events-none absolute rounded-full ${b.c} opacity-50 blur-3xl`}
            style={{ width: b.s, height: b.s, left: b.x, top: b.y }}
            animate={{ y: [0, -26, 0] }} transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: b.d }} />
        ))}
        <div className="wp-container relative grid items-center gap-10 pb-20 pt-14 sm:pt-20 lg:grid-cols-2 lg:gap-8 lg:pb-28 lg:pt-24">
          <Reveal>
            <span className="wp-chip"><Sparkles className="h-3.5 w-3.5 text-wp-gold" /> Thoughtfully created, beautifully packed</span>
            <h1 className="mt-5 font-display text-[2.6rem] leading-[1.05] tracking-[-0.01em] sm:text-6xl lg:text-[4.25rem]">
              {hero.headline ? (
                <>{hero.headline.split(".")[0]}.<br /><span className="text-wp-rose-strong">{hero.headline.split(".").slice(1).join(".").trim() || "Made personal."}</span></>
              ) : (
                <>Beautiful gifts,<br /><span className="text-wp-rose-strong">made personal.</span></>
              )}
            </h1>
            <p className="mt-5 max-w-md text-lg leading-relaxed text-wp-ink-muted">
              {hero.subheading || "Thoughtfully curated gift parcels of meaningful keepsakes — wrapped, ready to give, and easy to make truly personal."}
            </p>
            <div className="mt-8">
              <MagneticButton onClick={() => navigate("/shop")} data-testid="hero-explore-gifts-button"
                className="inline-flex items-center gap-2 rounded-xl bg-wp-rose-strong px-8 py-4 text-base font-semibold text-white shadow-wp-soft hover:brightness-110">
                Explore Gifts <ArrowRight className="h-5 w-5" />
              </MagneticButton>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-wp-ink-muted">
              <span className="flex items-center gap-1.5"><Gift className="h-4 w-4 text-wp-rose-strong" /> Gift-ready packaging</span>
              <span className="flex items-center gap-1.5"><Truck className="h-4 w-4 text-wp-rose-strong" /> Ships in 24–48h</span>
              <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-wp-rose-strong" /> Handwritten note included</span>
            </div>
          </Reveal>
          <div className="relative flex justify-center lg:justify-end">
            <GiftBox />
          </div>
        </div>
      </section>

      {/* ================= FEATURED COLLECTIONS ================= */}
      {featuredCollections.length > 0 && (
        <section className="wp-section">
          <div className="wp-container">
            <SectionHeading eyebrow="Find the perfect fit" title="Shop our collections" subtitle="Handpicked edits for every someone and every moment." align="center" />
            <div className="mt-10 grid grid-cols-2 gap-4 sm:gap-5 lg:grid-cols-3">
              {featuredCollections.map((c, i) => (
                <Reveal key={c.slug} delay={(i % 3) * 0.06}>
                  <Link to={`/shop?collection=${c.slug}`} data-testid={`home-collection-${c.slug}`}
                    className="group relative block overflow-hidden rounded-wp-xl border border-border bg-card shadow-wp-card transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-wp-lift">
                    <div className="relative aspect-[4/3] overflow-hidden bg-wp-cream-100">
                      <ProtectedImage src={c.image} alt={c.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-wp-ink/70 via-wp-ink/10 to-transparent" />
                      <div className="absolute inset-x-0 bottom-0 p-4 text-left text-white">
                        <h3 className="font-display text-xl leading-tight">{c.name}</h3>
                        {c.tagline && <p className="mt-0.5 text-xs text-white/85">{c.tagline}</p>}
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
            <div className="mt-10 text-center">
              <Button onClick={() => navigate("/shop")} variant="outline" className="rounded-xl border-wp-rose-strong/30 px-7 py-6 text-base text-wp-rose-strong hover:bg-wp-blush-50" data-testid="home-view-all-collections">
                View all gifts <ArrowRight className="ml-1.5 h-5 w-5" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* ================= FOUNDER COLLECTION ================= */}
      {founderTeaser.length > 0 && (
        <section className="wp-section bg-wp-surface">
          <div className="wp-container">
            <div className="mx-auto max-w-2xl text-center">
              <span className="wp-chip mx-auto"><Palette className="h-3.5 w-3.5 text-wp-gold" /> Limited Handmade Collection</span>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl">One-of-a-kind, made by hand</h2>
              <p className="mx-auto mt-3 max-w-xl text-wp-ink-muted">A tiny, hand-painted and hand-numbered collection by our founder. Each piece is unique — when they're gone, they're gone.</p>
            </div>
            <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              {founderTeaser.map((p, i) => (
                <Reveal key={p.id} delay={(i % 6) * 0.05}>
                  <Link to="/founder" data-testid={`home-founder-card-${p.id}`} className="group block overflow-hidden rounded-wp-lg border border-border bg-card shadow-wp-soft transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-wp-lift">
                    <div className="relative aspect-square overflow-hidden bg-wp-cream-100">
                      <ProtectedImage src={p.image} alt={p.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <Badge className="absolute left-2 top-2 border-0 bg-wp-rose-strong px-1.5 text-[10px] text-white"><Palette className="mr-0.5 h-2.5 w-2.5" /> Handmade</Badge>
                      {p.is_sold_out && <div className="absolute inset-0 grid place-items-center bg-wp-ink/50 text-xs font-semibold text-white">Sold Out</div>}
                    </div>
                    <div className="p-2.5">
                      <h3 className="truncate font-display text-sm leading-tight">{p.name}</h3>
                      <span className="price-num text-xs font-semibold text-wp-rose-strong">{formatINR(p.price)}</span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
            <div className="mt-8 text-center">
              <Button onClick={() => navigate("/founder")} variant="outline" className="rounded-xl" data-testid="home-founder-link">View the collection <ArrowRight className="ml-1 h-4 w-4" /></Button>
            </div>
          </div>
        </section>
      )}

      {/* ================= FEATURED GIFTS ================= */}
      {(featured.length > 0 || featuredFallback.length > 0) && (
        <section className="wp-section">
          <div className="wp-container">
            <SectionHeading eyebrow="Ready to gift" title="Featured gifts" subtitle="A handpicked selection of our curated gifts." align="center" />
            <div className="mt-10 grid grid-cols-2 gap-4 sm:gap-5 lg:grid-cols-4">
              {featured.length > 0
                ? featured.map((p, i) => <ParcelCard key={p.id} parcel={p} index={i} />)
                : featuredFallback.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
            <div className="mt-10 text-center">
              <Button onClick={() => navigate("/shop")} className="rounded-xl bg-wp-rose-strong px-7 py-6 text-base text-white hover:brightness-110" data-testid="home-explore-all-button">
                Explore all gifts <ArrowRight className="ml-1.5 h-5 w-5" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* ================= SPECIAL GIFTING: CORPORATE + CUSTOM SILVER ================= */}
      {(corporate?.enabled !== false || silver?.enabled !== false) && (
        <section className="wp-section" data-testid="home-special-gifting">
          <div className="wp-container">
            <SectionHeading eyebrow="Made-to-order gifting" title={content.special_gifting_title} subtitle={content.special_gifting_subtitle} align="center" />
            <div className="mt-10 grid items-stretch gap-6 md:grid-cols-2">
              {corporate?.enabled !== false && (
                <div className="group flex flex-col overflow-hidden rounded-wp-xl border border-border bg-card shadow-wp-soft transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-wp-lift" data-testid="home-corporate-card">
                  <div className="aspect-[16/9] w-full overflow-hidden bg-wp-cream-100">
                    <ProtectedImage src={corporate?.categories?.[0]?.image} alt="Corporate gifting" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="flex flex-1 flex-col p-6 md:p-7">
                    <span className="wp-label inline-flex items-center gap-1.5 text-xs text-wp-rose-strong"><Building2 className="h-3.5 w-3.5" /> Corporate Gifting</span>
                    <h3 className="mt-2 font-display text-2xl text-wp-ink">{content.corporate_card_title}</h3>
                    <p className="mt-2 flex-1 text-sm text-wp-ink-muted">{content.corporate_card_copy}</p>
                    <Button onClick={() => navigate("/corporate-gifting")} className="mt-5 w-full rounded-xl bg-wp-rose-strong py-6 text-base text-white hover:brightness-110 sm:w-auto sm:self-start sm:px-7" data-testid="home-corporate-cta">
                      Explore Corporate Gifting <ArrowRight className="ml-1.5 h-5 w-5" />
                    </Button>
                  </div>
                </div>
              )}
              {silver?.enabled !== false && (
                <div className="group flex flex-col overflow-hidden rounded-wp-xl border border-border bg-card shadow-wp-soft transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-wp-lift" data-testid="home-custom-silver-entry">
                  <div className="aspect-[16/9] w-full overflow-hidden bg-wp-cream-100">
                    <ProtectedImage src={silver?.samples?.[0]?.image} alt="Custom silver jewellery" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="flex flex-1 flex-col p-6 md:p-7">
                    <span className="wp-label inline-flex items-center gap-1.5 text-xs text-wp-rose-strong"><Sparkles className="h-3.5 w-3.5" /> Custom Silver Gifts</span>
                    <h3 className="mt-2 font-display text-2xl text-wp-ink">{content.silver_card_title}</h3>
                    <p className="mt-2 flex-1 text-sm text-wp-ink-muted">{content.silver_card_copy}</p>
                    <Button onClick={() => navigate("/custom-silver")} className="mt-5 w-full rounded-xl bg-wp-rose-strong py-6 text-base text-white hover:brightness-110 sm:w-auto sm:self-start sm:px-7" data-testid="home-custom-silver-cta">
                      Explore Custom Silver <ArrowRight className="ml-1.5 h-5 w-5" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ================= HOW IT WORKS ================= */}
      <section className="wp-section bg-wp-surface">
        <div className="wp-container">
          <SectionHeading eyebrow="Gifting, made effortless" title="How it works" align="center" />
          <div className="mt-10 grid gap-5 sm:grid-cols-3">
            {HOW_IT_WORKS.map((w, i) => (
              <Reveal key={w.title} delay={(i % 3) * 0.08}>
                <div className="relative h-full rounded-wp-xl border border-border bg-card p-7 text-center shadow-wp-soft">
                  <span className="absolute right-5 top-4 font-display text-3xl text-wp-gold-soft">{i + 1}</span>
                  <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-wp-blush-100 text-wp-rose-strong"><w.icon className="h-6 w-6" /></span>
                  <h3 className="mt-4 font-display text-xl">{w.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-wp-ink-muted">{w.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= REVIEWS ================= */}
      {showReviews && (
      <section className="wp-section" data-testid="reviews-carousel">
        <div className="wp-container">
          <SectionHeading eyebrow="Kind words" title="What gifters say" align="center" />
          <div className="mt-10">
            <Carousel opts={{ align: "start", loop: true }} className="px-2">
              <CarouselContent>
                {(reviews || []).map((r) => (
                  <CarouselItem key={r.id} className="sm:basis-1/2 lg:basis-1/3">
                    <div className="h-full rounded-wp-xl border border-border bg-card p-7 shadow-wp-card">
                      <div className="flex items-center gap-3">
                        <ProtectedImage src={r.avatar} alt={r.name} className="h-14 w-14 rounded-full object-cover ring-2 ring-wp-blush-100" />
                        <div>
                          <p className="font-display text-base">{r.name}</p>
                          <p className="text-xs text-wp-ink-muted">{r.city} · {r.parcel}</p>
                        </div>
                      </div>
                      <div className="mt-3 flex gap-0.5">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="h-4 w-4 fill-wp-gold text-wp-gold" />)}</div>
                      <Quote className="mt-3 h-6 w-6 text-wp-blush-200" />
                      <p className="mt-1 text-[15px] leading-relaxed text-wp-ink">{r.text}</p>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="hidden sm:flex" />
              <CarouselNext className="hidden sm:flex" />
            </Carousel>
          </div>
        </div>
      </section>
      )}

      {/* ================= INSTAGRAM ================= */}
      {ig.enabled && ig.tiles.length > 0 && (
        <section className="wp-section bg-wp-surface" data-testid="instagram-feed">
          <div className="wp-container">
            <div className="flex flex-col items-center text-center">
              <span className="grid h-12 w-12 place-items-center rounded-full bg-wp-blush-100 text-wp-rose-strong shadow-wp-soft"><Instagram className="h-6 w-6" /></span>
              <h2 className="mt-3 font-display text-2xl sm:text-3xl">Follow the little joys</h2>
              <p className="mt-2 text-sm text-wp-ink-muted">Real customer moments at <span className="font-medium text-wp-rose-strong">{ig.handle}</span></p>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {ig.tiles.map((t, i) => (
                <Reveal key={t.id} delay={(i % 4) * 0.05}>
                  <a href={ig.url} target="_blank" rel="noreferrer" data-testid={`instagram-tile-${i}`} className="group relative block aspect-square overflow-hidden rounded-wp-md shadow-wp-soft">
                    <ProtectedImage src={t.image} alt={t.caption} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-wp-ink/45 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                      <Instagram className="h-6 w-6 text-white" />
                      <span className="flex items-center gap-1 text-xs text-white/90"><Heart className="h-3.5 w-3.5 fill-white" /> {t.likes}</span>
                    </div>
                  </a>
                </Reveal>
              ))}
            </div>
            <div className="mt-8 text-center">
              <a href={ig.url} target="_blank" rel="noreferrer" data-testid="instagram-follow-button" className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-6 py-3 text-sm font-semibold text-wp-rose-strong shadow-wp-soft hover:bg-wp-blush-50">
                <Instagram className="h-4 w-4" /> Follow {ig.handle}
              </a>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
